import React, { useState } from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import IconItem from 'components/common/icon/IconItem';
import IconButton from 'components/common/IconButton';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import { useEffect } from 'react'
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import moment from 'moment';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';

const getColumns = (onEditHcp) => [
    {
        accessorKey: 'hcp',
        header: 'HCP',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'speciality',
        header: 'Speciality',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'address',
        header: 'Address',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'city',
        header: 'City',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'officephone',
        header: 'Office Phone',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: {
                className: 'text-start'
            }
        }
    },
    {
        accessorKey: 'Action',
        header: 'Edit',
        meta: {
            headerProps: {
                className: 'text-900 text-start'
            },
            cellProps: {
                className: 'align-items-center'
            }
        },
        enableSorting: false,
        cell: ({ row: { original } }) => {
            if (onEditHcp) {
                return (
                    <IconItem
                        tag="button"
                        icon={['far', 'edit']}
                        size="sm"
                        className="btn rounded-3 me-2 fs-11 align-items-center"
                        onClick={() => onEditHcp(original)}
                    />
                );
            }
            return (
                <Link to={{ pathname: paths.addEditHcp }} state={{
                    doc_id: original.doc_id,
                    firstname: original.firstname,
                    lastname: original.lastname,
                    address: original.address,
                    city: original.city,
                    state: original.state,
                    zip: original.zip,
                    gender: original.gender,
                    phone: original.officephone,
                    fax: original.fax,
                    speciality: original.speciality,
                    doctors_speciality_id: original.doctors_speciality_id,
                    isactive: original.isactive
                }}>
                    <IconItem
                        tag="button"
                        icon={['far', 'edit']}
                        size="sm"
                        className="btn rounded-3 me-2 fs-11 align-items-center"
                    />
                </Link>
            );
        }
    }
];

const HcpTable = (props) => {
    const { statusFilter = props.active_status, setStatusFilter, onAddHcp, onEditHcp, refreshTrigger } = props;
    const [allDoctors, setAllDoctors] = useState([]);
    const [hcpData, setHcpData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(true);

    const columns = React.useMemo(() => getColumns(onEditHcp), [onEditHcp]);

    useEffect(() => {
        async function fetchDoctorsData() {
            setLoading(true);
            let doctorSpeciality = [];
            let tokenStr = localStorage.getItem("accessToken");

            try {
                const resSpec = await axios.get(`${process.env.REACT_APP_SERVER_URL}` + "/doctors/list-doctor-speciality", { 
                    headers: { 'Authorization': `Bearer ${tokenStr}` } 
                });
                let objDocSpecialityData = resSpec.data.data || [];
                objDocSpecialityData.forEach((value) => {
                    doctorSpeciality.push({
                        key: value.id,
                        value: value.speciality,
                    });
                });
            } catch (err) {
                console.error("Error fetching doctor speciality:", err);
            }

            try {
                const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}` + "/doctors/list-doctors", { 
                    headers: { 'Authorization': `Bearer ${tokenStr}` } 
                });

                if (data.status) {
                    const doctorsData = data.data || [];
                    const mappedList = doctorsData.map((value) => {
                        let active_sts = value.is_active ? "active" : "inactive";
                        let docSpeciality = value.doctors_speciality_id != null 
                            ? doctorSpeciality.filter(item => Number(item.key) === Number(value.doctors_speciality_id)) 
                            : [];

                        return {
                            doc_id: value.id,
                            hcp: value.lastname + " " + value.firstname,
                            firstname: value.firstname,
                            lastname: value.lastname,
                            state: value.state,
                            zip: value.zip,
                            speciality: docSpeciality[0]?.value || 'NA',
                            doctors_speciality_id: value.doctors_speciality_id,
                            gender: value.gender,
                            fax: value.fax,
                            address: value.address,
                            city: value.city,
                            officephone: value.phone,
                            isactive: value.is_active,
                            active_sts: active_sts
                        };
                    });

                    setAllDoctors(mappedList);
                } else {
                    toast.info("No Doctors found in this list");
                    setAllDoctors([]);
                }
            } catch (err) {
                console.error("Error fetching doctors:", err);
                setAllDoctors([]);
            } finally {
                setLoading(false);
            }
        }

        fetchDoctorsData();
    }, [refreshTrigger]);

    // Local filtering based on statusFilter prop
    useEffect(() => {
        let filtered = [];
        const filterVal = (statusFilter || '').toLowerCase();

        if (filterVal === 'active' || filterVal === 'active-hcp') {
            filtered = allDoctors.filter(item => item.active_sts === 'active' || item.isactive === 1 || item.isactive === true);
        } else if (filterVal === 'inactive' || filterVal === 'inactive-hcp') {
            filtered = allDoctors.filter(item => item.active_sts === 'inactive' || item.isactive === 0 || item.isactive === false);
        } else {
            // 'all' or fallback
            filtered = [...allDoctors];
        }

        setHcpData(filtered);
        setError(filtered.length > 0);
    }, [allDoctors, statusFilter]);

    const table = useAdvanceTable({
        data: hcpData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    return (
        <AdvanceTableProvider {...table}>
            <Row className="g-2 align-items-center mb-3">
                <Col md={4}>
                    <AdvanceTableSearchBox placeholder="Search..." />
                </Col>
                {setStatusFilter && (
                    <Col md={4} className="d-flex align-items-center gap-2">
                        <Form.Label className="mb-0 text-nowrap fw-semi-bold fs-10">Filter:</Form.Label>
                        <Form.Select
                            size="sm"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ minWidth: '150px' }}
                        >
                            <option value="active">Active HCP</option>
                            <option value="inactive">Inactive HCP</option>
                            <option value="all">All HCP</option>
                        </Form.Select>
                    </Col>
                )}
                {onAddHcp && (
                    <Col md={4} className="ms-auto d-flex justify-content-end">
                        <IconButton
                            variant="primary"
                            size="sm"
                            icon="plus"
                            transform="shrink-3 down-2"
                            onClick={onAddHcp}
                        >
                            Add HCP
                        </IconButton>
                    </Col>
                )}
            </Row>
            <Row>
                <Col>
                    {loading ? (
                        <ShimmerUI />
                    ) : error ? (
                        <AdvanceTable
                            headerClassName="bg-200 text-nowrap align-middle text-center"
                            rowClassName="text-nowrap align-middle "
                            tableProps={{
                                size: 'lg',
                                striped: true,
                                className: 'fs-10 mb-0'
                            }}
                        />
                    ) : (
                        <div className="d-flex justify-content-center align-items-center text-center" style={{ height: '50px' }}>
                            <p className="text-danger fw-bold mb-0">There is no data available in the table</p>
                        </div>
                    )}
                </Col>
            </Row>
            <br />
            <Row>
                <Col>
                    <AdvanceTableFooter
                        rowInfo
                        navButtons
                        rowsPerPageSelection
                    />
                </Col>
            </Row>
        </AdvanceTableProvider>
    );
};

export default HcpTable;

